import { useMemo } from 'react';
import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';

export const resourceRelationQueryKeys = {
  all: ['resourceRelations'],
  opmContractNumberInfo: (params) => [...resourceRelationQueryKeys.all, 'opmContractNumberInfo', params],
  opmContractNumberInfoSummary: (drResourceSysId) => [
    ...resourceRelationQueryKeys.all,
    'opmContractNumberInfoSummary',
    drResourceSysId,
  ],
  opmDataLinkageComparison: (drResourceSysId) => [
    ...resourceRelationQueryKeys.all,
    'opmDataLinkageComparison',
    drResourceSysId,
  ],
  regionCluster: (params) => [...resourceRelationQueryKeys.all, 'regionCluster', params],
  drResourceStations: (drResourceSysId) => [...resourceRelationQueryKeys.all, 'drResource', drResourceSysId, 'stations'],
  stationChargers: (stationSysId) => [...resourceRelationQueryKeys.all, 'station', stationSysId, 'chargers'],
  cpoDataProblem: (params) => [...resourceRelationQueryKeys.all, 'cpoDataProblem', params],
};

// DR자원에 충전소 추가
export const useAddStationsToDrResource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ drResourceSysId, stationSysIds }) => {
      const response = await api.post(
        `/drivecore/resource/api/ResourceRelation/AddStationsToDrResource/${drResourceSysId}`,
        stationSysIds
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // DR자원 상세 정보 무효화
      queryClient.invalidateQueries({
        queryKey: ['drResourceDetail', variables.drResourceSysId]
      });
      // DR자원 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['drResources']
      });
      // 충전소 목록 무효화 (DrResourceSysId가 업데이트될 수 있음)
      queryClient.invalidateQueries({
        queryKey: ['stations']
      });
    },
    onError: (error) => {
      console.error('DR자원에 충전소 추가 실패:', error);
    }
  });
};

// DR자원에서 충전소 제거
export const useRemoveStationsFromDrResource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ drResourceSysId, stationSysIds }) => {
      const response = await api.post(
        `/drivecore/resource/api/ResourceRelation/RemoveStationsFromDrResource/${drResourceSysId}`,
        stationSysIds
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // DR자원 상세 정보 무효화
      queryClient.invalidateQueries({
        queryKey: ['drResourceDetail', variables.drResourceSysId]
      });
      // DR자원 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['drResources']
      });
      // 충전소 목록 무효화 (DrResourceSysId가 업데이트될 수 있음)
      queryClient.invalidateQueries({
        queryKey: ['stations']
      });
    },
    onError: (error) => {
      console.error('DR자원에서 충전소 제거 실패:', error);
    }
  });
};

// 충전소에 충전기 추가
export const useAddChargersToStation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ stationSysId, chargerIds }) => {
      const response = await api.post(
        `/drivecore/resource/api/ResourceRelation/AddChargersToStation/${stationSysId}`,
        chargerIds
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // 충전소 상세 정보 무효화
      queryClient.invalidateQueries({
        queryKey: ['stationDetail', variables.stationSysId]
      });
      // 충전소 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['stations']
      });
      // 충전기 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['chargers']
      });
    },
    onError: (error) => {
      console.error('충전소에 충전기 추가 실패:', error);
    }
  });
};

// 충전소에서 충전기 제거
export const useRemoveChargersFromStation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ stationSysId, chargerIds }) => {
      const response = await api.post(
        `/drivecore/resource/api/ResourceRelation/RemoveChargersFromStation/${stationSysId}`,
        chargerIds
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // 충전소 상세 정보 무효화
      queryClient.invalidateQueries({
        queryKey: ['stationDetail', variables.stationSysId]
      });
      // 충전소 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['stations']
      });
      // 충전기 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['chargers']
      });
    },
    onError: (error) => {
      console.error('충전소에서 충전기 제거 실패:', error);
    }
  });
};

// DR자원에 충전기 추가
export const useAddChargersToDrResource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ drResourceSysId, chargerIds }) => {
      const response = await api.post(
        `/drivecore/resource/api/ResourceRelation/AddChargersToDrResource/${drResourceSysId}`,
        chargerIds
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // DR자원 상세 정보 무효화
      queryClient.invalidateQueries({
        queryKey: ['drResourceDetail', variables.drResourceSysId]
      });
      // DR자원 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['drResources']
      });
      // 충전기 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['chargers']
      });
    },
    onError: (error) => {
      console.error('DR자원에 충전기 추가 실패:', error);
    }
  });
};

// DR자원에서 충전기 제거
export const useRemoveChargersFromDrResource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ drResourceSysId, chargerIds }) => {
      const response = await api.post(
        `/drivecore/resource/api/ResourceRelation/RemoveChargersFromDrResource/${drResourceSysId}`,
        chargerIds
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // DR자원 상세 정보 무효화
      queryClient.invalidateQueries({
        queryKey: ['drResourceDetail', variables.drResourceSysId]
      });
      // DR자원 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['drResources']
      });
      // 충전기 목록 무효화
      queryClient.invalidateQueries({
        queryKey: ['chargers']
      });
    },
    onError: (error) => {
      console.error('DR자원에서 충전기 제거 실패:', error);
    }
  });
};

// /drivecore/resource/api/OpmContractNumberInfo/(DResouceSysId)/(PowerContractNumber)
// 전력계약번호를 중심으로 관계된 충전소/충전기의 목록을 반환하는 API
// params: { DrResourceSysId, PowerContractNumber }
// PowerContractNumber는 쉼표로 여러 개일 수 있음 → 각각 호출 후 SecondItems 통합

/** OpmContractNumberInfo 응답 여러 개를 SecondItems 기준으로 합침(충전기 중복 제거) */
export const mergeOpmContractNumberInfoResponses = (list) => {
  const valid = (list || []).filter(Boolean);
  if (valid.length === 0) return undefined;

  const seen = new Set();
  const mergedItems = [];
  for (const d of valid) {
    const items = Array.isArray(d.SecondItems) ? d.SecondItems : [];
    for (const it of items) {
      const key =
        it.ChargerSysId != null
          ? `cs:${String(it.ChargerSysId)}`
          : it.ChargerId != null
            ? `cid:${String(it.ChargerId)}`
            : it.EVSEId != null
              ? `evse:${String(it.EVSEId)}`
              : `idx:${mergedItems.length}-${JSON.stringify(it)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      mergedItems.push(it);
    }
  }

  return {
    ...valid[0],
    SecondItems: mergedItems,
    SecondCount: mergedItems.length,
  };
};

export const useOpmContractNumberInfo = (params = {}) => {
  const { fetchEnabled = true, DrResourceSysId, PowerContractNumber } = params;

  const contractNumbers = useMemo(() => {
    if (PowerContractNumber == null || String(PowerContractNumber).trim() === '') return [];
    return [
      ...new Set(
        String(PowerContractNumber)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      ),
    ];
  }, [PowerContractNumber]);

  return useQueries({
    queries: contractNumbers.map((num) => ({
      queryKey: resourceRelationQueryKeys.opmContractNumberInfo({
        DrResourceSysId,
        PowerContractNumber: num,
      }),
      queryFn: () =>
        api.get(
          `/drivecore/resource/api/OpmContractNumberInfo/${encodeURIComponent(DrResourceSysId)}/${encodeURIComponent(num)}`
        ),
      staleTime: 5 * 60 * 1000,
      enabled: !!DrResourceSysId && !!num && fetchEnabled,
    })),
    combine: (results) => {
      if (contractNumbers.length === 0) {
        return {
          data: undefined,
          isPending: false,
          isLoading: false,
          isError: false,
          error: null,
        };
      }
      const merged = mergeOpmContractNumberInfoResponses(results.map((r) => r.data));
      return {
        data: merged,
        isPending: results.some((r) => r.isPending),
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
        error: results.find((r) => r.isError)?.error ?? null,
      };
    },
  });
};

/** 단일 계약번호용 OpmContractNumberInfo (선택 행 1건) — 쿼리 키는 useOpmContractNumberInfo와 동일해 캐시 공유 */
export const useOpmContractNumberInfoSingle = ({
  DrResourceSysId,
  PowerContractNumber,
  enabled = true,
} = {}) => {
  const num = String(PowerContractNumber ?? '').trim();
  return useQuery({
    queryKey: resourceRelationQueryKeys.opmContractNumberInfo({
      DrResourceSysId,
      PowerContractNumber: num,
    }),
    queryFn: () =>
      api.get(
        `/drivecore/resource/api/OpmContractNumberInfo/${encodeURIComponent(DrResourceSysId)}/${encodeURIComponent(num)}`
      ),
    staleTime: 5 * 60 * 1000,
    enabled: !!DrResourceSysId && !!num && enabled,
  });
};

// /drivecore/resource/api/RegionCluster/(DrResourceSysId)/StationCount/(RegionLevel)/(RegionName)
// 주어진 행정, 지역 단위에 충전소 수 반환
// params: { DrResourceSysId, RegionLevel, RegionName }


export const useRegionCluster = (params) => {
  return useQuery({
    queryKey: resourceRelationQueryKeys.regionCluster(params),
    queryFn: () => api.get(`/drivecore/resource/api/RegionCluster/${params.DrResourceSysId}/StationCount/${params.RegionLevel}/${params.RegionName}`, { params }),
    staleTime: 5 * 60 * 1000,
    enabled: !!params.DrResourceSysId && !!params.RegionLevel && !!params.RegionName,
  });
};


// /drivecore/resource/api/ResourceBulk/BulkAddStationChargerRelationToDrResource
// 충전소 계량기 이용하여 DR자원에 충전소 일괄 추가
export const useBulkAddStationChargerRelationToDrResource = () => {
  return useMutation({
    mutationFn: async ({ drResourceSysId, powerContractInfo, isDryRun }) => {
      const data = await api.post(
        `/drivecore/resource/api/ResourceBulk/BulkAddStationChargerRelationToDrResource`,
        {
          drResourceSysId,
          powerContractInfo,
          isDryRun
        }
      );
      return data;
    },
  });
};

// /drivecore/resource/api/ResourceBulk/BulkChargerPowerContractNumber/{DrResourceSysId}
// post test api
export const useBulkChargerPowerContractNumber = () => {
  return useMutation({
    mutationFn: async ({ drResourceSysId }) => {
      const data = await api.post(`/drivecore/resource/api/ResourceBulk/BulkChargerPowerContractNumber/${drResourceSysId}`);
      return data;
    },
  });
};


// /drivecore/resource/api/OpmContractNumberInfo/summary/{DrResourceSysId}
// OPM 전력계약 요약: TotalCount, ConnectedCount, DisconnectedCount
export const useOpmContractNumberInfoSummary = (params = {}, options = {}) => {
  const DrResourceSysId = params?.DrResourceSysId;
  const { enabled: enabledOpt, ...restOptions } = options;
  const enabled =
    !!DrResourceSysId && (enabledOpt !== undefined ? enabledOpt : true);

  return useQuery({
    queryKey: resourceRelationQueryKeys.opmContractNumberInfoSummary(DrResourceSysId),
    queryFn: () =>
      api.get(
        `/drivecore/resource/api/OpmContractNumberInfo/summary/${encodeURIComponent(DrResourceSysId)}`
      ),
    staleTime: 5 * 60 * 1000,
    ...restOptions,
    enabled,
  });
};

/**
 * OPM/제주 전기차 API와 시스템 계약번호 집합 비교용.
 * GET /drivecore/resource/api/OpmDataLinkage/comparison/{DrResourceSysId}
 * 응답 예: { OpmRegisteredTotal?, OpmApiContractNumbers?: string[], JejuEvContractNumbers?: string[] }
 */
export function normalizeOpmDataLinkageComparison(raw) {
  if (raw == null || typeof raw !== 'object') {
    return {
      opmRegisteredTotal: null,
      opmApiContractNumbers: [],
      jejuEvContractNumbers: [],
    };
  }
  const pickNumArr = (v) => {
    if (!Array.isArray(v)) return [];
    return v.map((x) => String(x ?? '').trim()).filter(Boolean);
  };
  const fromItems = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((row) =>
        String(row?.ContractNumber ?? row?.contractNumber ?? row ?? '').trim()
      )
      .filter(Boolean);
  };
  return {
    opmRegisteredTotal:
      raw.OpmRegisteredTotal != null
        ? Number(raw.OpmRegisteredTotal)
        : raw.opmRegisteredTotal != null
          ? Number(raw.opmRegisteredTotal)
          : null,
    opmApiContractNumbers: pickNumArr(
      raw.OpmApiContractNumbers ?? raw.opmApiContractNumbers ?? fromItems(raw.OpmApiItems)
    ),
    jejuEvContractNumbers: pickNumArr(
      raw.JejuEvContractNumbers ??
        raw.jejuEvContractNumbers ??
        fromItems(raw.JejuEvItems)
    ),
  };
}

export const useOpmDataLinkageComparison = (drResourceSysId, options = {}) => {
  const { enabled: enabledOpt, ...rest } = options;
  const enabled =
    !!drResourceSysId && (enabledOpt !== undefined ? enabledOpt : true);

  return useQuery({
    queryKey: resourceRelationQueryKeys.opmDataLinkageComparison(drResourceSysId),
    queryFn: async () => {
      const data = await api.get(
        `/drivecore/resource/api/OpmDataLinkage/comparison/${encodeURIComponent(drResourceSysId)}`
      );
      return normalizeOpmDataLinkageComparison(data);
    },
    staleTime: 2 * 60 * 1000,
    retry: false,
    ...rest,
    enabled,
  });
};


// /drivecore/resource/api/OpmContractNumberInfo/CpoDataProblem/(DrResourceSysId)?Days=N
// CPO 데이터 문제 조회
export const useCpoDataProblem = (params = {}) => {
  return useQuery({
    queryKey: resourceRelationQueryKeys.cpoDataProblem(params),
    queryFn: () => {
      const url = `/drivecore/resource/api/OpmContractNumberInfo/CpoDataProblem/${params.DrResourceSysId}`;
      return api.get(params.Days != null ? `${url}?Days=${params.Days}` : url);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!params.DrResourceSysId,
  });
};

/**
 * OPM 고객계약번호 단일 추가 API
 * @param {Object} params - 파라미터
 * @param {string} params.drResourceSysId - DR자원 시스템 ID
 * @param {string} params.contractNumber - 고객계약번호
 * @param {string} params.dataStartDate - 정보제공 시작일
 * @param {string} params.dataEndDate - 정보제공 종료일
 */
export const useCreateOpmContractNumberInfo = () => {
  return useMutation({
    mutationFn: async ({ drResourceSysId, contractNumber, dataStartDate, dataEndDate }) => {
      const data = await api.post(`/drivecore/resource/api/OpmContractNumberInfo`, { drResourceSysId, contractNumber, dataStartDate, dataEndDate });
      return data;
    },
  });
};

/**
 * OPM 고객계약번호 단일 삭제 API
 * @param {Object} params - 파라미터
 * @param {string} params.drResourceSysId - DR자원 시스템 ID
 * @param {string} params.contractNumber - 고객계약번호
 */
export const useDeleteOpmContractNumberInfo = () => {
  return useMutation({
    mutationFn: async ({ drResourceSysId, contractNumber }) => {
      const data = await api.delete(`/drivecore/resource/api/OpmContractNumberInfo/${encodeURIComponent(drResourceSysId)}/${encodeURIComponent(contractNumber)}`);
      return data;
    },
  });
};

/**
 * OPM 고객계약번호 정산대상 여부 수정 API
 * @param {Object} params - 파라미터
 * @param {string} params.drResourceSysId - DR자원 시스템 ID
 * @param {string} params.contractNumber - 고객계약번호
 * @param {boolean} params.value - 정산대상 여부
 */
export const useSetSettlementTargetValue = () => {
  return useMutation({
    mutationFn: async ({ drResourceSysId, contractNumber, value }) => {
      const data = await api.put(`/drivecore/resource/api/OpmContractNumberInfo/SetSettlementTargetValue/`,
        { drResourceSysId, contractNumber, value });
      return data;
    },
  });
};